/**
 * Route persistence.
 *
 * IndexedDB rather than localStorage: routes are structured, can be numerous,
 * and a passage plan is worth more than a UI preference — localStorage is a
 * synchronous 5 MB string bucket that browsers evict casually.
 *
 * The database itself is declared in `db.ts`, which tracks share. Two modules
 * opening the same database at different versions deadlocks the second one.
 *
 * Every read is defensive. A route that fails validation is dropped from the
 * listing rather than returned half-formed, because a route with a corrupt
 * waypoint would draw a leg across country.
 */

import { type Route, validateRoute } from '../core/route';
import { ROUTE_STORE as STORE, db, isStorageAvailable } from './db';

/** True when this browser can persist routes at all. */
export function isRouteStorageAvailable(): boolean {
  return isStorageAvailable();
}

export async function saveRoute(route: Route): Promise<void> {
  const problems = validateRoute(route);
  if (problems.length) {
    throw new Error(`Route cannot be saved: ${problems.join('; ')}`);
  }
  await (await db()).put(STORE, route);
}

export async function deleteRoute(id: string): Promise<void> {
  await (await db()).delete(STORE, id);
}

export async function loadRoute(id: string): Promise<Route | null> {
  const route = await (await db()).get(STORE, id);
  if (!route) return null;
  return validateRoute(route).length === 0 ? route : null;
}

/**
 * All stored routes, most recently edited first — the order a skipper wants,
 * since the route you are working on is the one you reach for.
 */
export async function listRoutes(): Promise<Route[]> {
  const all = await (await db()).getAllFromIndex(STORE, 'by-updated');
  return all
    .filter((route) => validateRoute(route).length === 0)
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

/** Drop everything. Used by tests and by an explicit "reset" in settings. */
export async function clearRoutes(): Promise<void> {
  await (await db()).clear(STORE);
}
